import { atom } from 'jotai';
import { CUSTOMER_CACHE_KEY, CUSTOMER_FAVORITE_KEY } from '@studio/constants/admin';
import type { Derived } from '@studio/types/admin';

const writeMetaCustomerData = (key: string, value: Derived.MetaCustomerData[]): void => {
  localStorage.setItem(key, JSON.stringify(value));
};

export const areAdminLogsEnabledAtom = atom(false);
export const customersAtom = atom<Derived.MetaCustomerData[]>(
  JSON.parse(localStorage.getItem(CUSTOMER_CACHE_KEY) ?? '[]') as Derived.MetaCustomerData[]
);
export const favoriteCustomersAtom = atom<Derived.MetaCustomerData[]>(
  JSON.parse(localStorage.getItem(CUSTOMER_FAVORITE_KEY) ?? '[]') as Derived.MetaCustomerData[]
);
export const frequentCustomersAtom = atom((get): Derived.MetaCustomerData[] => {
  const customers = get(customersAtom);

  return customers
    .slice()
    .reverse()
    .reduce<Derived.MetaCustomerData[]>((acc, customer) => {
      const foundIndex = acc.findIndex((item) => item.customerId === customer.customerId);

      if (foundIndex > -1) {
        const updated = [...acc];
        updated[foundIndex] = { ...updated[foundIndex], count: updated[foundIndex].count + 1 };
        return updated;
      }

      return [...acc, { ...customer, count: 1 }];
    }, [])
    .sort((a, b) => b.count - a.count);
});
export const isResiAdminAtom = atom(false);
export const setCustomerDataAtom = atom(
  null,
  (
    get,
    set,
    { currentUserCustomerId, customerInfo }: { currentUserCustomerId?: string; customerInfo: Derived.CustomerData }
  ): void => {
    if (customerInfo.customerId === currentUserCustomerId) return;

    const customers = get(customersAtom);
    const customerInfoWithDate: Derived.MetaCustomerData = {
      ...customerInfo,
      count: 0,
      date: new Date().toISOString(),
    };
    const updated = [...customers, customerInfoWithDate];
    set(customersAtom, updated);
    writeMetaCustomerData(CUSTOMER_CACHE_KEY, updated);
  }
);
export const setDemoViewAtom = atom(null, (_get, set, toggleDemoView: boolean): void => {
  set(showDemoViewAtom, toggleDemoView);
});
export const setViewAtom = atom(null, (_get, set, toggleAdminView: boolean): void => {
  set(showAdminViewAtom, toggleAdminView);
});
export const showAdminViewAtom = atom(false);
export const showDemoViewAtom = atom(false);
export const toggleFavoriteCustomerAtom = atom(null, (get, set, customerInfo: Derived.CustomerData): void => {
  const favoriteCustomers = get(favoriteCustomersAtom);
  const foundIndex = favoriteCustomers.findIndex((customer) => customer.customerId === customerInfo.customerId);

  if (foundIndex > -1) {
    const updated = [...favoriteCustomers];
    updated.splice(foundIndex, 1);
    set(favoriteCustomersAtom, updated);
    writeMetaCustomerData(CUSTOMER_FAVORITE_KEY, updated);
    return;
  }

  const customerInfoWithDate: Derived.MetaCustomerData = {
    ...customerInfo,
    count: 0,
    date: new Date().toISOString(),
  };
  const updated = [...favoriteCustomers, customerInfoWithDate];
  set(favoriteCustomersAtom, updated);
  writeMetaCustomerData(CUSTOMER_FAVORITE_KEY, updated);
});
export const uniqueCustomersAtom = atom((get): Derived.MetaCustomerData[] => {
  const customers = get(customersAtom);
  const uniqueById = new Map<string, Derived.MetaCustomerData>();

  customers.forEach((customer) => {
    const existing = uniqueById.get(customer.customerId);
    if (!existing || new Date(customer.date).getTime() > new Date(existing.date).getTime()) {
      uniqueById.set(customer.customerId, customer);
    }
  });

  return Array.from(uniqueById.values()).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
});
